/**
 * F/C/R/A keyboard shortcuts (spec §4.7 L298). Listens on window while
 * the component is mounted; skips events whose target is a form input
 * so typing numbers into the raise amount doesn't trigger a fold.
 */
import { useEffect } from 'react';
import { findLegal } from '@/engine/rules';
import type { Action, LegalAction, PlayerId } from '@/engine/types';

export type UseKeyboardShortcutsOptions = {
  enabled: boolean;
  playerId: PlayerId | null;
  legals: ReadonlyArray<LegalAction>;
  onAction: (action: Action) => void;
};

function shouldIgnore(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable === true
  );
}

export function useKeyboardShortcuts({
  enabled,
  playerId,
  legals,
  onAction,
}: UseKeyboardShortcutsOptions): void {
  useEffect(() => {
    if (!enabled || !playerId) return;
    const handler = (event: KeyboardEvent) => {
      if (shouldIgnore(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === 'f') {
        const fold = findLegal(legals, 'fold');
        if (!fold) return;
        event.preventDefault();
        onAction({ type: 'fold', playerId });
      } else if (key === 'c') {
        const check = findLegal(legals, 'check');
        const call = findLegal(legals, 'call');
        if (check) {
          event.preventDefault();
          onAction({ type: 'check', playerId });
        } else if (call) {
          event.preventDefault();
          onAction({ type: 'call', playerId, amount: call.amount ?? 0 });
        }
      } else if (key === 'r') {
        const raise = findLegal(legals, 'raise');
        const bet = findLegal(legals, 'bet');
        if (raise) {
          event.preventDefault();
          onAction({ type: 'raise', playerId, to: raise.min ?? 0 });
        } else if (bet) {
          event.preventDefault();
          onAction({ type: 'bet', playerId, amount: bet.min ?? 0 });
        }
      } else if (key === 'a') {
        const allin = findLegal(legals, 'allin');
        if (!allin) return;
        event.preventDefault();
        onAction({ type: 'allin', playerId, amount: allin.amount ?? 0 });
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [enabled, playerId, legals, onAction]);
}
